import "./index.scss";
import "../../../App.scss";
import { Link } from "react-router-dom";
import { useLocationParams, fetchImage, displayGameName } from "../../nonUIFuncs";
import ProgressBar from "../ProgressBar";
import PropTypes from "prop-types";
import { selectWidth } from "../../../Store/store";
import { useSelector } from "react-redux";

/**
 * Individual game type button component that displays a game with its progress and star rating.
 * Features a game icon, title, star indicators, and progress bar. Navigates to the selected game when clicked.
 * Supports responsive font sizing based on screen width and title length.
 *
 * @param {Object} props - Component props
 * @param {string} props.name - Display name of the game
 * @param {string} props.progress - Progress percentage as a string (e.g., "75%")
 * @param {number} props.stars - Number of stars earned (0-3)
 * @param {string} props.image - Filename of the game's icon image
 * @param {string} props.paramToSet - URL parameter name to set when navigating
 * @param {string} props.classNamePre - CSS class prefix for styling (default: "indivgame")
 * @param {Function} props.onmouseover - Mouse over event handler
 * @param {string} props.path - Navigation path when clicked
 * @param {Function} props.onClick - Click event handler
 * @param {Object} props.game - Game object containing additional data
 * @param {boolean} props.setKey - Whether to include Key parameter in navigation
 * @returns {JSX.Element} Interactive game button with icon, title, stars, and progress
 */

const IndivGame = ({
  name,
  progress,
  stars,
  image,
  paramToSet,
  classNamePre = "indivgame",
  onmouseover,
  path,
  onClick,
  game,
  setKey,
}) => {
  const width = useSelector(selectWidth);
  let titlePositioning = {
    left: "16%",
    right: "3%",
  };
  if (classNamePre === "unitsubmenu") {
    titlePositioning.left = "8%";
  }
  const adjustFontSize = (denom, low, high) => {
    let adjustedFontSize = width / denom / name.length;
    if (adjustedFontSize < low) {
      adjustedFontSize = low;
    } else if (adjustedFontSize > high) {
      adjustedFontSize = high;
    }
    return adjustedFontSize;
  };
  let keyParam = "";
  if (setKey) {
    keyParam = `&Key=${game.Key}`;
  }
  return (
    <Link
      onMouseOver={onmouseover}
      onFocus={onmouseover}
      to={{
        pathname: path,
        search: useLocationParams(`${paramToSet}=${name}${keyParam}`, []),
      }}
      onClick={onClick}
      className={`${classNamePre}_container hovercursor`}
    >
      <div className={`${classNamePre}_innercontainer`}>
        <div className={`${classNamePre}_badge`}>
          <div
            className={`${classNamePre}_gameimg`}
            style={{ backgroundImage: `url(${fetchImage(image)})` }}
          />
          <div className={`${classNamePre}_badgebanner`}>
            <div
              className={`${classNamePre}_${stars >= 1 ? "Star" : "Star_Base"}`}
            />
            <div
              className={`${classNamePre}_${stars >= 2 ? "Star" : "Star_Base"}`}
            />
            <div
              className={`${classNamePre}_${stars >= 3 ? "Star" : "Star_Base"}`}
            />
          </div>
        </div>
        <div className={`${classNamePre}_titlecontainer`}>
          <div
            style={{
              ...titlePositioning,
              position: "absolute",
              // backgroundColor: 'rgba(255,0,0,0.6)',
              display: "flex",
              justifyContent: "center",
            }}
          >
            <h2
              style={{
                fontSize:
                  classNamePre === "unitsubmenu"
                    ? adjustFontSize(6.7, 9, 25)
                    : adjustFontSize(4.7, 16, 48),
              }}
              className={`${classNamePre}_title`}
            >
              {displayGameName(name)}
            </h2>
          </div>
          <div className={`${classNamePre}_progressbarcontainer`}>
            <ProgressBar
              width="clamp(37px, 7.5vw, 98px)"
              id={`${name}_IndivGame`}
              progress={progress}
            />
          </div>
        </div>
      </div>
    </Link>
  );
};

export default IndivGame;

IndivGame.propTypes = {
  name: PropTypes.string,
  progress: PropTypes.string,
  stars: PropTypes.number,
  image: PropTypes.string,
  paramToSet: PropTypes.string,
  classNamePre: PropTypes.string,
  onmouseover: PropTypes.func,
  path: PropTypes.string,
  onClick: PropTypes.func,
};
